import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { fetchAllPages } from './fetch-all-pages';
import type { TeamUser, ChangeRequest } from '@/types';
import type { PaginationParams } from '@/types';
import type { SupervisorAssessment, AssessmentDetail, CompetencyDeclaration } from '@/types';

const KEYS = {
  team: (params?: PaginationParams) => ['supervisor', 'team', params] as const,
  teamAll: ['supervisor', 'team', 'all'] as const,
  changes: (params?: PaginationParams & { status?: string }) =>
    ['supervisor', 'changes', params] as const,
  assessments: (params?: Record<string, unknown>) => ['supervisor', 'assessments', params] as const,
  declarations: (params?: Record<string, unknown>) =>
    ['supervisor', 'declarations', params] as const,
};

export type PositionOption = {
  id: number;
  name: string;
  code: string | null;
};

export function usePositionsList() {
  return useQuery({
    queryKey: ['positions-list'],
    queryFn: () => fetchAllPages<PositionOption>('/positions'),
  });
}

export function useTeamUsers(params?: PaginationParams) {
  return useQuery({
    queryKey: KEYS.team(params),
    queryFn: () =>
      api.get<TeamUser[]>('/users', {
        page: params?.page,
        per_page: params?.per_page,
        search: params?.search,
      }),
    placeholderData: keepPreviousData,
  });
}

/** Equipo completo (todas las páginas) para selectores; useTeamUsers queda para tablas paginadas. */
export function useAllTeamUsers() {
  return useQuery({
    queryKey: KEYS.teamAll,
    queryFn: () => fetchAllPages<TeamUser>('/users'),
  });
}

export function useChanges(params?: PaginationParams & { status?: string }) {
  return useQuery({
    queryKey: KEYS.changes(params),
    queryFn: () =>
      api.get<ChangeRequest[]>('/changes', {
        page: params?.page,
        per_page: params?.per_page,
        status: params?.status,
        // #37 — "Aprobar ICNs" is a personal inbox: scope to the caller's own
        // approvals so other people's ICNs never appear. The API also enforces
        // this for non-admins, but requesting it explicitly keeps the inbox
        // personal even if the caller happens to hold an admin-tier role.
        mine: 'true',
      }),
    placeholderData: keepPreviousData,
  });
}

export function useApproveChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comments }: { id: number; comments?: string }) =>
      api.post(`/changes/${id}/approve`, { comments }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['supervisor', 'changes'] });
    },
  });
}

export function useRejectChange() {
  const qc = useQueryClient();
  return useMutation({
    // The API requires a non-empty `rejection_reason` (Zod .min(1)); sending the
    // wrong field name was the root cause of "Rechazar no funciona" (#5/#11).
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      api.post(`/changes/${id}/reject`, { rejection_reason: reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['supervisor', 'changes'] });
    },
  });
}

// Assessments for supervisor
export function useSupervisorAssessments(params?: {
  page?: number;
  per_page?: number;
  status?: string;
}) {
  return useQuery({
    queryKey: KEYS.assessments(params),
    queryFn: () =>
      api.get<SupervisorAssessment[]>('/assessments', {
        page: params?.page,
        per_page: params?.per_page,
        status: params?.status,
      }),
    placeholderData: keepPreviousData,
  });
}

export function useCreateAssessment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      assessed_id: number;
      assessment_type: string;
      criteria: Array<{ name: string; score: number; max_score: number; comments?: string }>;
    }) => api.post<AssessmentDetail>('/assessments', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['supervisor', 'assessments'] });
      qc.invalidateQueries({ queryKey: ['competency', 'assessments'] });
    },
  });
}

export function useCompleteAssessment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: {
        criteria: Array<{ name: string; score: number; max_score: number; comments?: string }>;
        overall_comments?: string;
      };
    }) => api.patch(`/assessments/${id}/complete`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['supervisor', 'assessments'] });
      qc.invalidateQueries({ queryKey: ['competency', 'assessments'] });
    },
  });
}

// Declarations for supervisor
export function useSupervisorDeclarations(params?: {
  page?: number;
  per_page?: number;
  status?: string;
}) {
  return useQuery({
    queryKey: KEYS.declarations(params),
    queryFn: () =>
      api.get<CompetencyDeclaration[]>('/competency', {
        page: params?.page,
        per_page: params?.per_page,
        status: params?.status,
      }),
    placeholderData: keepPreviousData,
  });
}

export function useCreateDeclaration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { user_id: number; position_id: number; declaration_type: string }) =>
      api.post<CompetencyDeclaration>('/competency', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['supervisor', 'declarations'] });
      qc.invalidateQueries({ queryKey: ['competency', 'declarations'] });
    },
  });
}
